import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import useTrans from '@hooks/useTrans'
import { api } from '@utils/index'
import { CONFEZIONAMENTI } from '@utils/endpoint'
import { cn, priceValutaNotation, sortCols, toCamelCase } from '@utils/safeTs'
import { ConfezionamentoType } from '@utils/types'
import { SearchField } from '@components/atoms'
import LoadingSvg from '@components/atoms/Loading/Loading'
import MyAccountTable, {
  MyAccountTableSortType,
} from '@components/atoms/MyAccountTable/MyAccountTable'
import styles from './MyAccountListiniConfezionamentoList.module.sass'

interface Props {
  className?: string
}

const MyAccountListiniConfezionamentoList = (props: Props) => {
  const { className = '' } = props

  const t = useTrans()
  const [loading, setLoading] = useState(true)
  const [listiniConfezionamento, setListiniConfezionamento] = useState<ConfezionamentoType[]>([])
  const [sort, setSort] = useState<MyAccountTableSortType>([])
  const [searchValue, setSearchValue] = useState('')
  const quantita = [] // [1, 50, 100, 250, 500, 1000, 2500, 5000, 10000] - per il momento non ci sono gli scaglioni

  // TODO - qui non ho valori per i confezionamenti, manca ancora la parte di listini?

  const cols: {
    key: string
    label?: string
    render: (item: ConfezionamentoType) => React.ReactNode
    style?: string
    getSortValue?: (item: ConfezionamentoType) => any
    align?: 'left' | 'right' | 'center'
  }[] = [
    {
      key: 'confezionamento',
      label: t('Confezionamento'),
      render: (item) => item.nome,
      getSortValue: (item) => item.nome,
    },
    {
      key: 'prezzo',
      label: t('Prezzo'),
      render: (item) => priceValutaNotation(item.prezzo),
      getSortValue: (item) => item.prezzo,
    },
    ...quantita.map((q) => ({
      key: q.toString(),
      label: `${q}+`,
      align: 'center' as 'left' | 'center' | 'right',
      style: 'value',
      getSortValue: (item) => item.values?.[q] || 0,
      render: (item) => priceValutaNotation(item.values?.[q]),
    })),
  ]

  const filteredResults = useMemo(
    () =>
      listiniConfezionamento.filter(
        (l) => !searchValue || l.nome.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
      ),
    [searchValue, listiniConfezionamento]
  )

  const loadListini = () => {
    setLoading(true)
    api
      .get(CONFEZIONAMENTI)
      .then((response) => {
        setListiniConfezionamento(toCamelCase(response.data || []) as ConfezionamentoType[])
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadListini()
  }, [])

  return (
    <div className={cn(styles.root, className)}>
      {loading ? (
        <LoadingSvg />
      ) : (
        <>
          <div className={styles.filters}>
            <div className={styles.rowFilters}>
              <SearchField
                size={'md'}
                placeholder={t('Cerca un confezionamento')}
                disabled={false}
                handleSearch={(v) => setSearchValue(v)}
                handleCancel={(v) => setSearchValue('')}
              />
            </div>
          </div>
          {filteredResults.length ? (
            <MyAccountTable
              headers={cols.map((c) => _.omit(c, ['render', 'style']))}
              sort={{
                keys: cols.filter((c) => !!c.getSortValue).map((c) => c.key),
                activeSort: sort,
                onSort: (s) => setSort(s),
              }}
            >
              {filteredResults
                .sort((a, b) => sortCols(cols, sort, a, b))
                .map((l) => (
                  <tr key={l.id}>
                    {cols.map((c) => (
                      <td
                        key={l.id + '_' + c.key}
                        className={cn(
                          styles.cell,
                          styles['cell--' + c.key],
                          c.style ? styles['cell--' + c.style] : ''
                        )}
                      >
                        {c.render(l)}
                      </td>
                    ))}
                  </tr>
                ))}
            </MyAccountTable>
          ) : (
            <div className={styles.empty}>{t('Nessun risultato')}</div>
          )}
        </>
      )}
    </div>
  )
}

export default MyAccountListiniConfezionamentoList
